import React from 'react'

import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import PropertyAccordion from '../ui/PropertyAccordion/PropertyAccordion'

interface IFilePropertyConfigProps {
  configuration: Record<string, any>
  onConfigChange: (configUpdater: (prev: any) => any) => void
}

const FILE_TYPE_PRESETS = [
  { label: 'Images', value: 'image/*' },
  { label: 'PDF', value: '.pdf' },
  { label: 'Documents', value: '.doc,.docx,.pdf' },
  { label: 'Spreadsheets', value: '.xls,.xlsx,.csv' },
] as const

const FilePropertyConfig: React.FC<IFilePropertyConfigProps> = ({
  configuration,
  onConfigChange,
}) => {
  const maxFiles = configuration?.maxFiles ?? ''
  const maxFileSizeMb = configuration?.maxFileSizeMb ?? ''
  const hasFileTypeRestriction = Array.isArray(configuration?.acceptedFileTypes)

  const handleMaxFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const parsed = parseInt(value, 10)
    onConfigChange(prev => ({
      ...prev,
      maxFiles:
        value === ''
          ? undefined
          : Number.isFinite(parsed) && parsed >= 1
            ? parsed
            : undefined,
    }))
  }

  const handleMaxFileSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const parsed = parseInt(value, 10)
    onConfigChange(prev => ({
      ...prev,
      maxFileSizeMb:
        value === ''
          ? undefined
          : Number.isFinite(parsed) && parsed >= 1
            ? parsed
            : undefined,
    }))
  }

  const handleToggleFileTypeRestriction = (enabled: boolean) => {
    onConfigChange(prev => ({
      ...prev,
      acceptedFileTypes: enabled ? [] : undefined,
    }))
  }

  const handleFileTypePresetClick = (presetValue: string) => {
    const current: string[] = configuration?.acceptedFileTypes || []
    const types = presetValue.split(',')
    const allPresent = types.every(t => current.includes(t))

    if (allPresent) {
      const updated = current.filter(t => !types.includes(t))
      onConfigChange(prev => ({
        ...prev,
        acceptedFileTypes: updated.length > 0 ? updated : undefined,
      }))
    } else {
      const merged = [...new Set([...current, ...types])]
      onConfigChange(prev => ({
        ...prev,
        acceptedFileTypes: merged,
      }))
    }
  }

  const isPresetActive = (presetValue: string): boolean => {
    const current: string[] = configuration?.acceptedFileTypes || []
    return presetValue.split(',').every(t => current.includes(t))
  }

  return (
    <PropertyAccordion
      title={Localization.translator.settings}
      isDefaultExpanded
    >
      <div className="tw:flex tw:flex-col tw:gap-4 tw:px-4">
        <StruxhubInput
          label="Max files"
          value={String(maxFiles)}
          onChange={handleMaxFilesChange}
          type="number"
          min={1}
          placeholder="Unlimited"
        />

        <StruxhubInput
          label="Max file size (MB)"
          value={String(maxFileSizeMb)}
          onChange={handleMaxFileSizeChange}
          type="number"
          min={1}
          placeholder="No limit"
        />

        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className="tw:flex tw:items-center tw:justify-between tw:px-2.5">
            <span className="tw:text-body-md tw:text-md3-on-surface">
              Restrict file types
            </span>
            <Switch
              checked={hasFileTypeRestriction}
              onCheckedChange={handleToggleFileTypeRestriction}
            />
          </div>

          {hasFileTypeRestriction && (
            <div className="tw:flex tw:flex-wrap tw:gap-2 tw:px-2.5">
              {FILE_TYPE_PRESETS.map(preset => (
                <button
                  key={preset.value}
                  type="button"
                  className={`tw:px-3 tw:py-1 tw:rounded-full tw:border tw:border-solid tw:text-body-sm tw:cursor-pointer ${
                    isPresetActive(preset.value)
                      ? 'tw:bg-md3-secondary-container tw:text-md3-on-secondary-container tw:border-md3-secondary-container'
                      : 'tw:bg-transparent tw:text-md3-on-surface-variant tw:border-md3-outline hover:tw:bg-md3-on-surface/4'
                  }`}
                  onClick={() => handleFileTypePresetClick(preset.value)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </PropertyAccordion>
  )
}

export default FilePropertyConfig
